import { userPrivileges, userTier } from '@apollo-internal/cache';
import { useQuery, useReactiveVar } from '@apollo/client';
import { getDataNotFoundPage } from '@shared/services/getDataNotFoundPageService';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { PAGE_VIEW_MODES } from '../../../../constants/page-view-modes';
import ApolloError from '@shared/components/ApolloError';
import OrgUnitBreadcrumbAnnotation from '@shared/components/breadcrumb/annotations/OrgUnitBreadcrumbAnnotation';
import GET_ORG_UNIT_INFO from '@shared/queries/getOrgUnitInfoQuery';
import { checkPointData, initialCheckPointData } from '../../checkPointsCache';
import GET_CHECK_POINT_BY_ID from '../../queries/getCheckPoint';
import AddCheckpointPage from '../addCheckPoint/AddCheckpointPage';
import EditCheckpointPage from '../editCheckpoint/EditCheckpointPage';
import CheckPointMapper from '../mappers/checkPointMapper';

/**
 * @typedef {object} CheckpointPageProps
 * @property {*} [pageMode]
 */

/**
 * @param {CheckpointPageProps} props
 */
const CheckpointPage = props => {
  const { orgUnit, appraisal, checkPointId } = useParams();
  const pageMode = props.pageMode;

  const orgUnitId = parseInt(orgUnit);
  const selectedAppraisalId = parseInt(appraisal);
  const selectedCheckPointId = parseInt(checkPointId);
  const previousLink = `/check-points/${orgUnitId}`;

  const checkPointCache = useReactiveVar(checkPointData);
  const tier = useReactiveVar(userTier);
  const privileges = useReactiveVar(userPrivileges);

  const isEdit = pageMode === PAGE_VIEW_MODES.EDIT;
  const RenderComponent = isEdit ? EditCheckpointPage : AddCheckpointPage;

  const {
    data: orgUnitData,
    error: orgUnitError,
    loading: orgUnitLoading
  } = useQuery(GET_ORG_UNIT_INFO, {
    variables: { id: orgUnitId }
  });

  const orgUnitName = orgUnitData && orgUnitData.mddapOrgUnit && orgUnitData.mddapOrgUnit.Name;
  const orgName =
    orgUnitData && orgUnitData.mddapOrgUnit && orgUnitData.mddapOrgUnit.MddapOrganization.Name;
  const renderedOrgUnit = orgUnitData && (
    <OrgUnitBreadcrumbAnnotation orgUnit={orgUnitData.mddapOrgUnit} key={orgUnitName} />
  );

  const {
    data,
    error: checkPointError,
    loading: checkPointLoading
  } = useQuery(GET_CHECK_POINT_BY_ID, {
    variables: {
      checkPointId: selectedCheckPointId
    }
  });

  useEffect(() => {
    if (!data || !data.checkPoint || !_.isEqual(checkPointCache, initialCheckPointData)) {
      return;
    }

    const checkPoint = CheckPointMapper.checkPointToCacheData(
      data.checkPoint,
      selectedAppraisalId,
      orgUnitId,
      pageMode
    );

    checkPointData(checkPoint);
  }, [checkPointCache, data, orgUnitId, pageMode, selectedAppraisalId, selectedCheckPointId]);

  if ((!isEdit && data?.checkPoint?.IsDraft === false) || (isEdit && data?.checkPoint?.IsDraft)) {
    return <Navigate to={previousLink} />;
  }

  if (
    orgUnitData?.mddapOrgUnit === null ||
    data?.checkPoint === null ||
    (isEdit &&
      data &&
      (!data.checkPointEditRemovalPermission.canUserEditOrRemoveSubmittedCheckPoint ||
        !data.checkPointEditRemovalPermission.isSubmittedCheckPointEditableOrRemovable))
  ) {
    return <Navigate to={getDataNotFoundPage(tier)} />;
  }

  if (orgUnitError || checkPointError) {
    return <ApolloError />;
  }

  return (
    <RenderComponent
      dataCollectionDate={data?.checkPoint?.DataCollectionDate}
      orgName={orgName}
      orgUnitId={orgUnitId}
      orgUnitName={orgUnitName}
      renderedOrgUnit={renderedOrgUnit}
      appraisalId={selectedAppraisalId}
      checkPointId={selectedCheckPointId}
      privileges={privileges}
      tier={tier}
      checkPointLoading={checkPointLoading}
      orgUnitLoading={orgUnitLoading}
    />
  );
};

export default CheckpointPage;
